import { useEffect, useState } from "react";
import {
  EnvelopeSimple, CheckCircle, XCircle, User, ArrowClockwise, PaperPlaneTilt, Users, PaperPlaneRight,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card, Loading } from "@/components/riq";
import SortableTable from "@/components/SortableTable";
import EmailComposeDialog from "@/components/EmailComposeDialog";
import { EmailProviderSettings } from "@/components/EmailProviderSettings";
import { EmailSuppressions } from "@/components/EmailSuppressions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const CAT_COLORS = ["#6E3FFC", "#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#EC4899", "#8B5CF6",
  "#14B8A6", "#F97316", "#6366F1", "#84CC16", "#06B6D4", "#D946EF", "#EAB308", "#64748B", "#22C55E", "#A0AEC0"];

function fmt(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d)) return String(ts).slice(0, 16).replace("T", " ");
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StatCard({ label, value, tone, testId }) {
  const colors = { ok: "text-emerald-600", bad: "text-red-500", primary: "text-riq-primary" };
  return (
    <Card className="!p-4" data-testid={testId}>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 font-head text-2xl font-bold ${colors[tone] || "text-neutral-800"}`}>{value}</div>
    </Card>
  );
}

function StackedBar({ categories, catIndex, total }) {
  if (!total) return <div className="h-2 rounded-full bg-neutral-100" />;
  const entries = Object.entries(categories || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-neutral-100" title={entries.map(([k, v]) => `${k}: ${v}`).join(", ")}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ width: `${(v / total) * 100}%`, background: CAT_COLORS[(catIndex[k] ?? 0) % CAT_COLORS.length] }} />
      ))}
    </div>
  );
}

const PROVIDERS = [
  { key: "ses", label: "Amazon SES", color: "#F59E0B" },
  { key: "resend", label: "Resend", color: "#6E3FFC" },
];

function ProviderSplit({ providers }) {
  const total = (providers.ses || 0) + (providers.resend || 0);
  return (
    <Card data-testid="email-provider-breakdown">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-neutral-700">By provider</div>
        {providers.failovers > 0 && (
          <span data-testid="email-provider-failovers" className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
            {providers.failovers} SES failover{providers.failovers === 1 ? "" : "s"} to Resend
          </span>
        )}
      </div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-neutral-100">
        {total > 0 && PROVIDERS.map((p) => (
          <div key={p.key} className="transition-[width] duration-500" style={{ width: `${((providers[p.key] || 0) / total) * 100}%`, background: p.color }} />
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        {PROVIDERS.map((p) => (
          <div key={p.key} data-testid={`email-provider-${p.key}`} className="flex items-center gap-2 text-sm">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />
            <span className="text-neutral-700">{p.label}</span>
            <b className="ml-auto tabular-nums text-neutral-800">{(providers[p.key] || 0).toLocaleString()}</b>
            <span className="w-10 text-right text-xs text-muted-foreground tabular-nums">{total ? `${Math.round(((providers[p.key] || 0) / total) * 100)}%` : "—"}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

const provLine = (p = {}) => [p.ses ? `SES ${p.ses}` : null, p.resend ? `Resend ${p.resend}` : null].filter(Boolean).join(" · ");

function EmailReport() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  const load = () => { setData(null); api.get(`/admin/email-stats?days=${days}`).then((r) => setData(r.data)).catch(() => setData(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (data === false) return <Card><div className="py-6 text-center text-muted-foreground">Could not load email stats.</div></Card>;
  if (!data) return <Loading />;

  const catIndex = {};
  data.categories.forEach((c, i) => { catIndex[c.key] = i; });
  const maxCat = Math.max(1, ...data.categories.map((c) => c.total));

  const dayRows = [...data.by_day].reverse().filter((d) => d.total > 0);
  const monthRows = [...data.by_month].reverse();

  return (
    <div className="space-y-5" data-testid="email-report">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-head text-lg font-bold text-riq-primary flex items-center gap-2"><EnvelopeSimple size={20} weight="duotone" /> Email report</h3>
        <div className="flex items-center gap-1 rounded-full border border-riq-border p-0.5">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`email-range-${d}`} onClick={() => setDays(d)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${days === d ? "bg-riq-primary text-white" : "text-neutral-500 hover:text-riq-primary"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatCard testId="email-stat-sent" label={`Sent · last ${data.window_days}d`} value={data.totals.sent.toLocaleString()} tone="primary" />
        <StatCard testId="email-stat-delivered" label="Delivered" value={data.totals.total ? `${Math.round((data.totals.sent / data.totals.total) * 100)}%` : "—"} tone="ok" />
        <StatCard testId="email-stat-failed" label="Failed" value={data.totals.failed.toLocaleString()} tone={data.totals.failed ? "bad" : undefined} />
      </div>

      {data.providers && <ProviderSplit providers={data.providers} />}

      {/* By category */}
      <Card>
        <div className="text-sm font-semibold text-neutral-700 mb-3">By category · last {data.window_days} days</div>
        {data.categories.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">No emails recorded in this window yet.</div>
        ) : (
          <div className="space-y-2.5">
            {data.categories.map((c) => (
              <div key={c.key} className="flex items-center gap-3" data-testid={`email-cat-${c.key}`}>
                <div className="w-40 shrink-0 text-sm text-neutral-700 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: CAT_COLORS[catIndex[c.key] % CAT_COLORS.length] }} />
                  <span className="truncate">{c.label}</span>
                </div>
                <div className="flex-1 h-2.5 rounded-full bg-neutral-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(c.total / maxCat) * 100}%`, background: CAT_COLORS[catIndex[c.key] % CAT_COLORS.length] }} />
                </div>
                <div className="w-12 text-right text-sm font-semibold tabular-nums text-neutral-800">{c.total.toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* By day */}
        <Card className="!p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-riq-border text-sm font-semibold text-neutral-700">By day</div>
          <div className="max-h-80 overflow-y-auto divide-y divide-riq-border">
            {dayRows.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground">No emails in this window.</div> :
              dayRows.map((d) => (
                <div key={d.day} className="px-4 py-2.5" data-testid={`email-day-${d.day}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-neutral-700">{d.day}</span>
                    <span className="tabular-nums"><span className="text-xs text-muted-foreground mr-2">{provLine(d.providers)}</span><b className="text-neutral-800">{d.total}</b>
                      {d.failed > 0 && <span className="text-red-500 ml-2">{d.failed} failed</span>}</span>
                  </div>
                  <div className="mt-1.5"><StackedBar categories={d.categories} catIndex={catIndex} total={d.total} /></div>
                </div>
              ))}
          </div>
        </Card>

        {/* By month */}
        <Card className="!p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-riq-border text-sm font-semibold text-neutral-700">By month</div>
          <div className="max-h-80 overflow-y-auto divide-y divide-riq-border">
            {monthRows.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground">No emails yet.</div> :
              monthRows.map((m) => (
                <div key={m.month} className="px-4 py-2.5" data-testid={`email-month-${m.month}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-neutral-700">{m.month}</span>
                    <span className="tabular-nums"><span className="text-xs text-muted-foreground mr-2">{provLine(m.providers)}</span><b className="text-neutral-800">{m.total}</b>
                      {m.failed > 0 && <span className="text-red-500 ml-2">{m.failed} failed</span>}</span>
                  </div>
                  <div className="mt-1.5"><StackedBar categories={m.categories} catIndex={catIndex} total={m.total} /></div>
                </div>
              ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default function AdminEmails() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [groupOpen, setGroupOpen] = useState(false);

  const load = () => { setRows(null); api.get("/admin/email/log").then((r) => setRows(r.data)).catch(() => setRows(false)); };
  useEffect(() => { load(); }, []);

  if (rows === false) return <div className="text-muted-foreground">Could not load email history.</div>;

  const columns = [
    { key: "at", label: "When", type: "date", render: (r) => <span className="text-muted-foreground whitespace-nowrap">{fmt(r.at)}</span> },
    { key: "sent_by_email", label: "Sent by", type: "text", render: (r) => <span className="truncate max-w-[180px] inline-block align-middle">{r.sent_by_email || "—"}</span> },
    { key: "subject", label: "Subject", type: "text", render: (r) => <span className="font-medium text-riq-primary truncate max-w-[280px] inline-block align-middle">{r.subject}</span> },
    { key: "total", label: "Recipients", type: "number", align: "right" },
    {
      key: "sent", label: "Delivered", type: "number", align: "right",
      render: (r) => (
        <span className="inline-flex items-center gap-1.5 justify-end">
          <span className="inline-flex items-center gap-0.5 text-emerald-600 font-semibold"><CheckCircle size={14} weight="fill" />{r.sent}</span>
          {r.failed_count > 0 && <span className="inline-flex items-center gap-0.5 text-red-500 font-semibold"><XCircle size={14} weight="fill" />{r.failed_count}</span>}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-8" data-testid="admin-emails">
      <EmailProviderSettings />
      <EmailSuppressions />
      <EmailReport />

      <div className="flex items-center justify-between pt-2 border-t border-riq-border">
        <h3 className="font-head text-lg font-bold text-riq-primary flex items-center gap-2 mt-4"><PaperPlaneRight size={20} weight="duotone" /> Emails you've sent</h3>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{rows ? `${rows.length} email${rows.length === 1 ? "" : "s"} sent` : "Loading…"}</div>
        <div className="flex items-center gap-2">
          <button data-testid="emails-group-send" onClick={() => setGroupOpen(true)}
            className="flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-4 py-1.5 text-xs font-semibold hover:opacity-90">
            <Users size={14} weight="bold" /> Email a group
          </button>
          <button data-testid="emails-refresh" onClick={load} className="flex items-center gap-1.5 rounded-full border border-riq-border px-3.5 py-1.5 text-xs font-medium text-neutral-600 hover:border-riq-primary">
            <ArrowClockwise size={13} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      <EmailComposeDialog open={groupOpen} groupMode onClose={() => { setGroupOpen(false); load(); }} />

      {!rows ? <Loading /> : rows.length === 0 ? (
        <Card>
          <div className="py-12 text-center">
            <EnvelopeSimple size={40} weight="duotone" className="text-riq-primary/40 mx-auto mb-3" />
            <div className="font-head font-semibold text-riq-primary">No emails sent yet</div>
            <div className="text-sm text-muted-foreground mt-1">Emails you send to users from the Users tab will appear here.</div>
          </div>
        </Card>
      ) : (
        <Card className="!p-0 overflow-hidden">
          <SortableTable
            columns={columns}
            rows={rows}
            defaultSort={[{ key: "at", dir: "desc" }]}
            rowKey="id"
            onRowClick={(r) => setOpen(r)}
            testIdPrefix="email-log-row"
            empty="No emails sent yet."
          />
        </Card>
      )}

      <EmailDetailDialog log={open} onClose={() => setOpen(null)} />
    </div>
  );
}

function EmailDetailDialog({ log, onClose }) {
  return (
    <Dialog open={!!log} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="email-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PaperPlaneTilt size={20} weight="duotone" className="text-riq-primary" /> {log?.subject}</DialogTitle>
          <DialogDescription className="sr-only">Sent email details and per-recipient delivery</DialogDescription>
        </DialogHeader>
        {log && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
              <span>Sent {fmt(log.at)}</span>
              <span className="flex items-center gap-1"><User size={13} /> {log.sent_by_email}</span>
              <span className="text-emerald-600 font-semibold">{log.sent} delivered</span>
              {log.failed_count > 0 && <span className="text-red-500 font-semibold">{log.failed_count} failed</span>}
            </div>

            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1.5">Message</div>
              <div className="rounded-xl border border-riq-border bg-secondary/30 p-3 text-sm text-neutral-700 whitespace-pre-wrap max-h-48 overflow-y-auto">{log.message}</div>
            </div>

            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1.5">Recipients ({log.recipients?.length || 0})</div>
              <div className="rounded-xl border border-riq-border divide-y divide-riq-border max-h-56 overflow-y-auto">
                {(log.recipients || []).map((r, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 text-sm">
                    {r.status === "delivered"
                      ? <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0" />
                      : <XCircle size={16} weight="fill" className="text-red-500 shrink-0" />}
                    <span className="flex-1 min-w-0 truncate">{r.email || r.name || r.user_id}</span>
                    {r.status === "failed" && r.error && <span className="text-[11px] text-red-500 truncate max-w-[160px]" title={r.error}>{r.error}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
